import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import api from "./api";
import "../estilo.css";

export default function Planos() {
  const { t, i18n } = useTranslation();
  const [produtos, setProdutos] = useState([]);
  const [pacotes, setPacotes] = useState([]);
  const [selectedRobots, setSelectedRobots] = useState(new Set());
  const [selectedCombo, setSelectedCombo] = useState(false);
  const [selectedTier, setSelectedTier] = useState(null);
  const [loading, setLoading] = useState(true);
  const locale = i18n.resolvedLanguage === "en" ? "en-US" : "pt-BR";
  const formatCurrency = (value, currency = "BRL") =>
    new Intl.NumberFormat(locale, { style: "currency", currency }).format(Number(value || 0));
  const getProductDescription = (product) =>
    i18n.resolvedLanguage === "en"
      ? t(`plans.productDescriptions.${String(product.codigo || "").toLowerCase()}`, {
          defaultValue: product.descricao,
        })
      : product.descricao;

  useEffect(() => {
    carregarDados();
  }, []);

  const carregarDados = async () => {
    try {
      const [resProd, resPac] = await Promise.all([
        api.get("/api/produtos-ativos"),
        api.get("/api/pacotes-ativos"),
      ]);
      setProdutos(resProd.data || []);
      setPacotes(resPac.data || []);
    } catch (err) {
      console.error("Erro ao carregar produtos/pacotes:", err);
    } finally {
      setLoading(false);
    }
  };

  const toggleRobot = (id) => {
    setSelectedCombo(false);
    setSelectedRobots((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const toggleCombo = () => {
    setSelectedCombo(!selectedCombo);
    if (!selectedCombo) setSelectedRobots(new Set());
  };

  const limparSimulacao = () => {
    setSelectedRobots(new Set());
    setSelectedCombo(false);
    setSelectedTier(null);
  };

  const calcularResumo = () => {
    if (!selectedTier) return null;

    const pacote = pacotes[0];

    if (selectedCombo && pacote) {
      const preco = pacote.precos[selectedTier];
      if (!preco) return null;
      return {
        items: [
          {
            nome: pacote.nome,
            brl: Number(preco.valor_brl),
            usd: Number(preco.valor_usd),
          },
        ],
        slots: preco.slots_inclusos,
        totalBrl: Number(preco.valor_brl),
        totalUsd: Number(preco.valor_usd),
        extraBrl: Number(preco.valor_extra_brl),
        extraUsd: Number(preco.valor_extra_usd),
        isCombo: true,
      };
    }

    if (selectedRobots.size > 0) {
      const items = [];
      let totalBrl = 0;
      let totalUsd = 0;
      let slots = 0;
      let extraBrl = 0;
      let extraUsd = 0;

      selectedRobots.forEach((id) => {
        const prod = produtos.find((p) => p.id === id);
        if (prod && prod.precos[selectedTier]) {
          const preco = prod.precos[selectedTier];
          items.push({
            nome: prod.nome,
            brl: preco.valor_brl,
            usd: preco.valor_usd,
          });
          totalBrl += Number(preco.valor_brl);
          totalUsd += Number(preco.valor_usd);
          slots = preco.slots_inclusos;
          extraBrl = Number(preco.valor_extra_brl);
          extraUsd = Number(preco.valor_extra_usd);
        }
      });

      return {
        items,
        slots,
        totalBrl,
        totalUsd,
        extraBrl,
        extraUsd,
        isCombo: false,
      };
    }

    return null;
  };

  const resumo = calcularResumo();
  const pacote = pacotes[0];

  const calcEconomiaBrl = () => {
    if (!selectedTier || !pacote) return 0;
    const individual = produtos.reduce((sum, p) => {
      return (
        sum +
        (p.precos[selectedTier]
          ? Number(p.precos[selectedTier].valor_brl)
          : 0)
      );
    }, 0);
    const combo = pacote.precos[selectedTier]
      ? Number(pacote.precos[selectedTier].valor_brl)
      : 0;
    return individual - combo;
  };

  if (loading) {
    return (
      <div id="planos" className="container-fluid planos-section">
        <div className="text-center">
          <h2>{t('plans.loading')}</h2>
        </div>
      </div>
    );
  }

  return (
    <div id="planos" className="container-fluid planos-section">
      <div className="text-center planos-header">
        <h2>{t('plans.title')}</h2>
        <h4>{t('plans.subtitle')}</h4>
        <p className="planos-subtitle">{t('plans.includes')}</p>
      </div>

      <div className="planos-step">
        <h3 className="planos-step-title">
          <span className="planos-step-num">1</span>
          {t('plans.chooseRobots')}
        </h3>

        <div className="planos-robots-grid">
          {produtos.map((prod) => (
            <div
              key={prod.id}
              className={`planos-robot-card ${
                selectedRobots.has(prod.id) ? "selected" : ""
              }`}
              onClick={() => toggleRobot(prod.id)}
            >
              <div className="planos-robot-check">
                {selectedRobots.has(prod.id) ? "✓" : ""}
              </div>
              <div className="planos-robot-icon">
                {prod.codigo === "ea"
                  ? "EA"
                  : prod.codigo === "bias"
                  ? "BI"
                  : "RK"}
              </div>
              <h4 className="planos-robot-name">{prod.nome}</h4>
              <p className="planos-robot-desc">{getProductDescription(prod)}</p>
              <div className="planos-robot-price">
                {selectedTier && prod.precos?.[selectedTier]
                  ? formatCurrency(prod.precos[selectedTier].valor_brl)
                  : t('plans.fromPrice', { price: formatCurrency(20) })}
                <span>{t('plans.perMonth')}</span>
              </div>
            </div>
          ))}
        </div>

        {pacote && (
          <div
            className={`planos-combo-banner ${selectedCombo ? "selected" : ""}`}
            onClick={toggleCombo}
          >
            <div className="planos-combo-left">
              <strong className="planos-combo-title">
                {pacote.nome}
                <span className="planos-combo-badge">
                  {t('plans.savings', { value: formatCurrency(calcEconomiaBrl()) })}
                </span>
              </strong>
              <p className="planos-combo-detail">
                {i18n.resolvedLanguage === 'en' ? t('plans.packageDescription') : pacote.descricao}
              </p>
            </div>
            <div className="planos-combo-right">
              {selectedTier && pacote.precos[selectedTier] ? (
                <>
                  <span className="planos-combo-original">
                    {formatCurrency(produtos
                      .reduce(
                        (s, p) =>
                          s +
                          (p.precos[selectedTier]
                            ? Number(p.precos[selectedTier].valor_brl)
                            : 0),
                        0
                      ))}
                  </span>
                  <span className="planos-combo-final">
                    {formatCurrency(pacote.precos[selectedTier].valor_brl)}
                    {t('plans.perMonth')}
                  </span>
                </>
              ) : (
                <span className="planos-combo-final">{t('plans.selectTier')}</span>
              )}
            </div>
          </div>
        )}

        {selectedRobots.size === produtos.length && !selectedCombo && (
          <div className="planos-combo-hint" onClick={toggleCombo}>
            {t('plans.allSelected', { value: formatCurrency(calcEconomiaBrl()) })}
          </div>
        )}
      </div>

      <div className="planos-step">
        <h3 className="planos-step-title">
          <span className="planos-step-num">2</span>
          {t('plans.operationSize')}
        </h3>
        <p className="planos-step-sub">
          {t('plans.accountQuestion')}
        </p>

        <div className="planos-tiers-grid">
          {["starter", "trader", "pro"].map((tier) => {
            const tierData = {
              starter: {
                nome: t('plans.tiers.starter.name'),
                slots: 2,
                desc: t('plans.tiers.starter.description'),
              },
              trader: {
                nome: t('plans.tiers.trader.name'),
                slots: 5,
                desc: t('plans.tiers.trader.description'),
              },
              pro: {
                nome: t('plans.tiers.pro.name'),
                slots: 10,
                desc: t('plans.tiers.pro.description'),
              },
            }[tier];

            const preco = produtos[0]?.precos?.[tier];

            return (
              <div
                key={tier}
                className={`planos-tier-card ${
                  selectedTier === tier ? "selected" : ""
                }`}
                onClick={() => setSelectedTier(selectedTier === tier ? null : tier)}
              >
                <h4 className="planos-tier-name">{tierData.nome}</h4>
                <p className="planos-tier-slots">{t('plans.mt5Accounts', { count: tierData.slots })}</p>
                <p className="planos-tier-desc">{tierData.desc}</p>
                {preco && (
                  <>
                    <div className="planos-tier-price">
                      {formatCurrency(preco.valor_brl)}
                      <span>{t('plans.perRobot')}</span>
                    </div>
                    <p className="planos-tier-extra">
                      {t('plans.extraAccount', { value: formatCurrency(preco.valor_extra_brl) })}
                    </p>
                    <p className="planos-tier-usd">
                      {formatCurrency(preco.valor_usd, 'USD')}{t('plans.perRobot')}
                    </p>
                  </>
                )}
              </div>
            );
          })}
        </div>
      </div>

      <div className="planos-resumo">
        {resumo ? (
          <>
            {resumo.items.map((item, i) => (
              <div key={i} className="planos-resumo-row">
                <span>{item.nome}</span>
                <span>{formatCurrency(item.brl)}</span>
              </div>
            ))}
            <div className="planos-resumo-row">
              <span>{t('plans.tierSummary', {
                tier: selectedTier ? t(`plans.tiers.${selectedTier}.name`) : '',
                count: resumo.slots,
              })}</span>
              <span></span>
            </div>
            <div className="planos-resumo-total">
              <span>{t('plans.monthlyTotal')}</span>
              <span className="planos-resumo-valor">
                {formatCurrency(resumo.totalBrl)}
              </span>
            </div>
            <div className="planos-resumo-usd">
              <span>{t('plans.internationalClients')}</span>
              <span>{formatCurrency(resumo.totalUsd, 'USD')}</span>
            </div>
          </>
        ) : (
          <p className="planos-resumo-empty">
            {t('plans.emptySummary')}
          </p>
        )}
      </div>

      <div
        className="text-center"
        style={{
          marginTop: 20,
          display: "flex",
          gap: 12,
          justifyContent: "center",
          flexWrap: "wrap",
        }}
      >
        <a
          href={resumo ? "/cadastro" : undefined}
          onClick={(event) => {
            if (!resumo) {
              event.preventDefault();
              return;
            }
            sessionStorage.setItem("quantumPlanSelection", JSON.stringify({
              selectedRobots: Array.from(selectedRobots),
              selectedCombo,
              selectedTier,
              pacoteId: selectedCombo && pacote ? pacote.id : null,
            }));
          }}
          className={`btn btn-primary btn-lg ${!resumo ? "disabled" : ""}`}
          aria-disabled={!resumo}
        >
          {t('plans.createAccount')}
        </a>

        <button
          type="button"
          className="btn btn-default btn-lg"
          onClick={limparSimulacao}
        >
          {t('plans.clear')}
        </button>
      </div>

      <div className="planos-disclaimer" role="note">
        <strong className="planos-disclaimer-title">{t('plans.important')}</strong>
        <p>{t('plans.disclaimer')}</p>
      </div>
    </div>
  );
}
